import { useState, type FormEvent } from 'react';
import { Banner } from '../ui/banner';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input } from '../ui/input';
import { NumberStepper } from '../ui/number-stepper';
import { SegmentedControl } from '../ui/segmented-control';
import { TagInput } from '../ui/tag-input';
import { useToast } from '../ui/toast';
import { api } from '../../lib/api';
import {
  emptyStaffForm,
  ROLE_OPTIONS,
  STAFF_EMAIL_DOMAIN,
  toStaffRequest,
  type StaffFormErrors,
  type StaffFormValues,
} from '../../lib/staff-form';

export interface AddStaffDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the new person's email after the invite is sent (e.g. to refresh the roster). */
  onInvited?: (email: string) => void;
}

function Form({ onClose, onInvited }: Pick<AddStaffDialogProps, 'onClose' | 'onInvited'>) {
  const toast = useToast();
  const [values, setValues] = useState<StaffFormValues>(emptyStaffForm);
  const [errors, setErrors] = useState<StaffFormErrors>({});
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof StaffFormValues>(key: K, value: StaffFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));
  const roleHelp = ROLE_OPTIONS.find((r) => r.value === values.role)?.help;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(undefined);
    const result = toStaffRequest(values);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const res = await api.api.admin.staff.$post({ json: result.body });
      if (res.status === 201) {
        toast({ title: 'Invite sent', description: `${result.body.email} will get a link to set a password.` });
        onInvited?.(result.body.email);
        onClose();
        return;
      }
      if (res.status === 409) setErrors({ emailLocal: 'Someone with this email already has an account.' });
      else if (res.status === 403) setFormError('Only admins can add staff.');
      else if (res.status === 401) setFormError('Your session expired. Please sign in again.');
      else setFormError('We couldn’t send the invite. Please try again.');
    } catch {
      setFormError('Can’t reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-(--mbm-space-4)">
      {formError ? <Banner tone="error">{formError}</Banner> : null}

      <Input
        label="Full name"
        variant="compact"
        autoComplete="off"
        value={values.name}
        error={errors.name}
        onChange={(e) => set('name', e.target.value)}
      />

      <div className="grid gap-(--mbm-space-3) sm:grid-cols-2">
        {/* The local part plus the fixed staff domain, as in the design. */}
        <Input
          label="Work email"
          variant="compact"
          autoComplete="off"
          autoCapitalize="none"
          value={values.emailLocal}
          error={errors.emailLocal}
          addon={`@${STAFF_EMAIL_DOMAIN}`}
          onChange={(e) => set('emailLocal', e.target.value.replace(/@.*$/, ''))}
        />
        {/* TODO(api): staff have no phone number column; drawn as designed, not editable until there is one. */}
        <Input label="Phone (optional)" variant="compact" disabled placeholder="+91 98765 43210" />
      </div>

      <div className="flex flex-col gap-(--mbm-space-1-5)">
        <SegmentedControl
          label="Role"
          shape="compact"
          options={ROLE_OPTIONS}
          value={values.role}
          onChange={(role) => set('role', role)}
        />
        {roleHelp ? <p className="text-caption text-text-tertiary">{roleHelp}</p> : null}
      </div>

      {/* TODO(product): the design shows Department as a select; the list of departments is not defined, so it is free text. */}
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-2">
        <Input
          label="Department"
          variant="compact"
          value={values.department}
          placeholder="e.g. Video"
          error={errors.department}
          onChange={(e) => set('department', e.target.value)}
        />
        <Input
          label="Title"
          variant="compact"
          value={values.title}
          placeholder="e.g. Video editor"
          error={errors.title}
          onChange={(e) => set('title', e.target.value)}
        />
      </div>

      <TagInput
        compact
        label="Skills"
        placeholder="Type a skill and press Enter"
        value={values.skills}
        onChange={(skills) => set('skills', skills)}
      />

      <div className="grid items-start gap-(--mbm-space-3) sm:grid-cols-2">
        <NumberStepper
          compact
          label="Max active tasks"
          helperText="Dispatch warns above this. Default 5."
          value={values.maxActiveTasks}
          onChange={(n) => set('maxActiveTasks', n)}
        />
        {/* TODO(api): there is no per-person daily capacity yet (the agency default applies); drawn as designed. */}
        <Input
          label="Daily capacity (work units)"
          variant="compact"
          disabled
          value="6.0 · agency default"
          readOnly
          helperText="Uses the agency default from Capacity rules."
        />
      </div>

      <div className="flex flex-col-reverse gap-(--mbm-space-2) sm:flex-row sm:justify-end">
        <Button variant="ghost" size="small" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" size="small" loading={busy}>
          Send invite
        </Button>
      </div>
    </form>
  );
}

/** Figma "Add staff" (A04.1): provisions an account and emails a link to set a password. Admin only (the API enforces it). */
export function AddStaffDialog({ open, onClose, onInvited }: AddStaffDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="wide"
      density="form"
      title="Add staff"
      description="We email them a link to set their own password."
    >
      <Form onClose={onClose} onInvited={onInvited} />
    </Dialog>
  );
}
